import React, { useState, useEffect, useMemo, lazy, Suspense, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { checkUserInLocalStorage } from "../redux/slices/userSlice";
import { fetchMasterSession } from "../redux/slices/masterSessionSlice";
import { loadMasterData } from "../Components/MasterDataLoader/MasterDataLoade";
import { fetchMasterByLinkKey } from "../redux/slices/masterSlice";
import OtpVerification from "../Pages/Registration/OtpVerification";
import ClientDashboard from "../Pages/Clients/ClientDashboard/ClientDashboard";
import LazyErrorScreen from "../Pages/MasterDashboard/LazyErrorScreen/LazyErrorScreen";
import PaymentReturnTakbull from "../Components/TakbullCredentialsModal/PaymentReturnTakbull";
import PrivacyPolicy from "../Pages/Registration/PrivacyPolicy/PrivacyPolicy";
import TermsOfUse from "../Pages/Registration/TermsOfUse/TermsOfUse";

const Login = lazy(() => import("../Pages/Registration/Login"));
const Connect = lazy(() => import("../Components/AllPaySendLinkButton/Connect"));
const ErrorScreen = lazy(() => import("../Pages/MasterDashboard/ErrorScreen/ErrorScreen"));
const OAuthRedirectHandler = lazy(() => import("../Components/GoogleCalendarConnect/OAuthRedirectHandler"));
const GoogleCalendarConnect = lazy(() => import("../Components/GoogleCalendarConnect/GoogleCalendarConnect"));
const CreateMaster = lazy(() => import("../Pages/Registration/CreateMaster/CreateMaster"));
const Speciality = lazy(() => import("../Pages/Registration/CreateMaster/Speciality"));
const BusinessLocation = lazy(() => import("../Pages/Registration/BusinessLocation/BusinessLocation"));
const BusinessHours = lazy(() => import("../Pages/Registration/BusinessHours/BusinessHours"));
const ServicesPanel = lazy(() => import("../Pages/MasterDashboard/Services/ServicesPanel"));
const ClientsPanel = lazy(() => import("../Pages/Clients/ClientsPanel").then(m => ({ default: m.ClientsPanel })));
const PaymentReturnStaff = lazy(() => import("../Components/PaymentReturn/PaymentReturnStaff"));
const PaymentReturnMessages = lazy(() => import("../Components/PaymentReturn/PaymentReturnMessages"));
const PaymentReturnSubscription = lazy(() => import("../Components/PaymentReturn/PaymentReturnSubscription"));
const ConfettiScreen = lazy(() => import("../Components/ConfettiScreen/ConfettiScreen"));
const PaymentSuccessful = lazy(() => import("../Components/Billing/PaymentSuccessful"));
const PaymentFailed = lazy(() => import("../Components/Billing/PaymentFailed"));
const RegistrationDone = lazy(() => import("../Pages/Registration/RegistrationDone/RegistrationDone"));
const MasterDashboard = lazy(() => import("../Pages/MasterDashboard/MasterDashboard/MasterDashboard"));

// ─── Лоадер ───────────────────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
    <div style={{ width: 36, height: 36, border: "3px solid #e5e7eb", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ─── ErrorBoundary ────────────────────────────────────────────────────────────
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error, info) {
    console.error("[LazyErrorBoundary]", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <LazyErrorScreen
          onReload={() => {
            this.setState({ hasError: false });
            window.location.reload();
          }}
        />
      );
    }

    return this.props.children;
  }
}

// ─── Helper ───────────────────────────────────────────────────────────────────
const getMasterLinkKeyFromPath = (pathname) => {
  const seg = pathname.split("/").filter(Boolean);
  const idx = seg.indexOf("master");
  if (idx === -1) return null;
  return seg[idx + 1] || null;
};

// ─── Navigation ───────────────────────────────────────────────────────────────
const Navigation = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const supportedLanguages = ["en", "ru", "he"];
    const currentPath = location.pathname;
    const pathLang = currentPath.split("/")[1];

    const savedLanguage = localStorage.getItem("language");
    const browserLanguage = navigator.language?.split("-")[0] || "ru";

    const defaultLang = supportedLanguages.includes(savedLanguage)
      ? savedLanguage
      : supportedLanguages.includes(browserLanguage)
        ? browserLanguage
        : "ru";

    if (
      !supportedLanguages.includes(pathLang) &&
      !currentPath.startsWith("/oauth2callback")
    ) {
      navigate(
        {
          pathname: `/${defaultLang}${currentPath}`,
          search: location.search,
          hash: location.hash,
        },
        { replace: true }
      );
    }
  }, [
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  useEffect(() => {
    if (lang && i18n.language !== lang) { i18n.changeLanguage(lang); localStorage.setItem("language", lang); }
  }, [lang, i18n]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && savedLanguage !== i18n.language) i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const pathname = location.pathname.replace(/\/+$/, "");

    const isAllPayConnect =
      pathname === "/connect" ||
      pathname.endsWith("/connect");

    if (isAllPayConnect) {
      console.log("[ALLPAY] Skip global auth session check");
      return;
    }

    dispatch(checkUserInLocalStorage());
    dispatch(fetchMasterSession()).catch(() => { });
  }, [dispatch, location.pathname]);

  const [masterPath, setMasterPath] = useState(null);
  const [masterCheck, setMasterCheck] = useState({ key: null, status: "idle" });
  const master = useSelector((state) => state.master?.master || null);

  // Держим ref с реально актуальным ключом (не зависит от замыкания эффекта).
  // Нужен, чтобы после гонки запросов можно было "самоисцелиться" —
  // если пришёл устаревший ответ и перетёр стор чужими данными,
  // мы тут же перезапрашиваем актуального мастера ещё раз.
  const latestKeyRef = useRef(null);

  useEffect(() => {
    const key = getMasterLinkKeyFromPath(location.pathname);
    latestKeyRef.current = key;

    if (!key) { setMasterPath(null); setMasterCheck({ key: null, status: "idle" }); return; }
    setMasterPath(key);
    setMasterCheck((prev) => (prev.key === key && prev.status !== "idle") ? prev : { key, status: "loading" });

    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(fetchMasterByLinkKey(key));
        if (cancelled) return;

        const rejected = res?.meta?.requestStatus === "rejected";
        if (rejected || !res?.payload) { setMasterCheck({ key, status: "notFound" }); return; }

        // Гонка: пока этот запрос летал, пользователь мог уже кликнуть
        // на другого мастера/сотрудника. Если ключ с тех пор изменился —
        // этот ответ устарел. Он мог успеть перезаписать state.master
        // (fulfilled в masterSlice ничего не проверяет), поэтому явно
        // перезапрашиваем актуального мастера ещё раз, чтобы вытолкнуть
        // устаревшие данные из стора.
        if (latestKeyRef.current !== key) {
          dispatch(fetchMasterByLinkKey(latestKeyRef.current));
          return;
        }

        setMasterCheck({ key, status: "ok" });
      } catch { if (!cancelled) setMasterCheck({ key, status: "error" }); }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, dispatch]);

  const [masterId, setMasterId] = useState(null);
  useEffect(() => {
    if (masterCheck.status === "ok" && master?.uid) setMasterId(String(master.uid));
    else setMasterId(null);
  }, [masterCheck.status, master?.uid]);

  const masterSession = useSelector((s) => s.masterSession || null);
  const userSession = useSelector((s) => s.userSession || null);

  const isAuthed = useMemo(() => {
    const hasMaster = !!masterSession?.isAuthenticated;
    const hasUser = !!userSession?.isAuthenticated;
    return hasMaster || hasUser;
  }, [masterSession, userSession]);

  useEffect(() => {
    if (!masterId || !isAuthed) return;
    loadMasterData(masterId, dispatch);
  }, [masterId, isAuthed, dispatch]);

  // ── defaultLang для редиректов ────────────────────────────────────────────
  const defaultLang = (() => {
    const saved = localStorage.getItem("language");
    const browser = navigator.language?.split("-")[0] || "ru";
    return ["en", "ru", "he"].includes(saved) ? saved
      : ["en", "ru", "he"].includes(browser) ? browser : "ru";
  })();

  return (
    <div dir={currentLang?.includes("he") ? "rtl" : "ltr"}>
      <LazyErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Редиректы с корня ────────────────────────────────── */}
            <Route path="/" element={<Navigate to={`/${defaultLang}/login`} replace />} />
            <Route path="/:lang" element={<Navigate to={`/${defaultLang}/login`} replace />} />
            <Route path="/:lang/" element={<Navigate to={`/${defaultLang}/login`} replace />} />

            {/* ── Auth ─────────────────────────────────────────────── */}
            <Route path="/:lang/login" element={<Login />} />
            <Route path="/:lang/otp-verification" element={<OtpVerification />} />

            {/* ── Регистрация мастера ───────────────────────────────── */}
            <Route path="/:lang/master/:masterId/create-master" element={<CreateMaster />} />
            <Route path="/:lang/master/:masterId/speciality" element={<Speciality />} />
            <Route path="/:lang/master/:masterId/business-location" element={<BusinessLocation />} />
            <Route path="/:lang/master/:masterId/business-hours" element={<BusinessHours />} />
            <Route path="/:lang/master/:masterId/registration-done" element={<RegistrationDone />} />
            <Route path="/:lang/master/:masterId/confetti" element={<ConfettiScreen />} />

            {/* ── Панели кабинета ───────────────────────────────────── */}
            <Route path="/:lang/master/:masterId/services" element={<ServicesPanel />} />
            <Route path="/:lang/master/:masterId/clients" element={<ClientsPanel />} />

            <Route path="/:lang/master/:masterId/master-dashboard" element={<MasterDashboard />} />
            <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
            <Route path="/:lang/terms" element={<TermsOfUse />} />


            {/* ── Public Master ─────────────────────────────────────── */}
            <Route
              path="/:lang/master/:masterId/:section?"
              element={
                masterCheck.status === "loading" || masterCheck.status === "idle"
                  ? <PageLoader />
                  : <ErrorScreen />
              }
            />

            {/* ── Google Calendar ───────────────────────────────────── */}
            <Route path="/oauth2callback" element={<OAuthRedirectHandler />} />
            <Route path="/:lang/google-calendar" element={<GoogleCalendarConnect />} />

            {/* ── Connect ───────────────────────────────────────────── */}
            <Route path="/connect" element={<Connect />} />
            <Route path="/:lang/connect" element={<Connect />} />

            {/* ── Платежи ───────────────────────────────────────────── */}
            <Route path="/payment-return" element={<PaymentReturnStaff />} />
            <Route path="/payment-return-messages" element={<PaymentReturnMessages />} />
            <Route path="/payment-return-subscription" element={<PaymentReturnSubscription />} />
            <Route path="/payment/success" element={<PaymentSuccessful />} />
            <Route path="/:lang/payment/success" element={<PaymentSuccessful />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />
            <Route path="/:lang/payment/failed" element={<PaymentFailed />} />

            <Route
              path="/:lang/payment-return-takbull"
              element={<PaymentReturnTakbull />}
            />

            <Route
              path="payment-return-takbull"
              element={<PaymentReturnTakbull />}
            />
            {/* ── Клиенты ───────────────────────────────────────────────── */}

            <Route path="/:lang/user/myappointments" element={<ClientDashboard />} />


            {/* ── 404 ───────────────────────────────────────────────── */}
            <Route path="*" element={<ErrorScreen />} />

          </Routes>
        </Suspense>
      </LazyErrorBoundary>
    </div>
  );
};

export default Navigation;